import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import SpinnerSmall from 'components/SpinnerSmall'
import AddImage from './AddImage'
import ResetSettingsItemModal from './accountInformation/ResetSettingsItemModal'
import { setViewResetItemModal, updateFullName, updateUsername } from 'features/settings/settingSlice'
import { updatePassword } from 'features/profile/profileSlice'
import { updateUser } from 'features/auth/authSlice'
import { useTranslation } from 'react-i18next'

export default function ProfileAccountSettings() {
    const { t } = useTranslation(['settings', 'common'])
    const dispatch = useDispatch()
    const { profile, isUpdateLoading } = useSelector((state) => state.profile)
    const { user } = useSelector((state) => state.auth)
    const {
        viewResetItemModal,
        isResetEmailSuccess,
        isUpdateFullNameLoading,
        isUpdateFullNameSuccess,
        isUpdateUsernameLoading,
        isUpdateUsernameSuccess,
        newEmail,
        newFullName,
        newUsername
    } = useSelector((state) => state.settings)

    const [fullName, setFullName] = useState(user.fullName || '')
    const [username, setUsername] = useState(user.name || '')
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        newPassword2: ''
    })

    useEffect(() => {
        setFullName(user.fullName || '')
    }, [user.fullName])

    useEffect(() => {
        setUsername(user.name || '')
    }, [user.name])

    useEffect(() => {
        if(isUpdateFullNameSuccess && newFullName){
            toast.success(t('settings:accountEdit.displayNameUpdated'))
            setFullName(newFullName)
            dispatch(updateUser({
                type: 'fullName',
                value: newFullName
            }))
        }
    }, [dispatch, isUpdateFullNameSuccess, newFullName])

    useEffect(() => {
        if(isUpdateUsernameSuccess && newUsername){
            toast.success(t('settings:accountEdit.usernameUpdated'))
            setUsername(newUsername)
            dispatch(updateUser({
                type: 'name',
                value: newUsername
            }))
        }
    }, [dispatch, isUpdateUsernameSuccess, newUsername])

    const handleNameSubmit = () => {
        const trimmedName = fullName.trim()

        if(!trimmedName){
            return toast.error(t('settings:accountEdit.nameRequired'))
        }

        if(trimmedName === user.fullName){
            return toast.error(t('settings:accountEdit.noChanges'))
        }

        if(trimmedName.length < 3){
            return toast.error(t('settings:accountEdit.nameMinimum'))
        }

        dispatch(updateFullName({
            fullName: trimmedName
        }))
    }

    const handlePasswordChange = (e) => {
        setPasswords((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
    }

    const handleUsernameChange = (e) => {
        setUsername(e.target.value.replace(/\s/g, ''))
    }

    const handleUsernameSubmit = () => {
        const trimmedUsername = username.trim()

        if(!trimmedUsername){
            return toast.error(t('settings:accountEdit.usernameRequired'))
        }

        if(trimmedUsername === user.name){
            return toast.error(t('settings:accountEdit.noChanges'))
        }

        if(trimmedUsername.length < 3){
            return toast.error(t('settings:accountEdit.usernameMinimum'))
        }

        if(trimmedUsername.length >= 20){
            return toast.error(t('settings:accountEdit.usernameMaximum'))
        }

        dispatch(updateUsername({
            username: trimmedUsername
        }))
    }

    const handlePasswordSubmit = (e) => {
        e.preventDefault()

        const { currentPassword, newPassword, newPassword2 } = passwords

        if(currentPassword.length < 8 || newPassword.length < 8 || newPassword2.length < 8){
            return toast.error(t('settings:accountEdit.passwordMinimum'))
        }

        if(newPassword !== newPassword2){
            return toast.error(t('settings:accountEdit.passwordMismatch'))
        }

        if(currentPassword === newPassword){
            return toast.error(t('settings:accountEdit.passwordDifferent'))
        }

        dispatch(updatePassword(passwords))
        setPasswords({
            currentPassword: '',
            newPassword: '',
            newPassword2: ''
        })
    }

    return (
        <ProfileSettingsContainer>
            <ProfileHeader>
                <div>
                    <ProfileTitle>{t('settings:sections.profile')}</ProfileTitle>
                    <ProfileSubtitle>{t('settings:accountEdit.description')}</ProfileSubtitle>
                </div>
            </ProfileHeader>

            <ProfileCard>
                <SectionHeader>
                    <SectionTitle>{t('settings:accountEdit.profileImage')}</SectionTitle>
                    <SectionCopy>{t('settings:accountEdit.profileImageHelp')}</SectionCopy>
                </SectionHeader>
                <AddImage hideHeader />
            </ProfileCard>

            <ProfileCard>
                <SectionHeader>
                    <SectionTitle>{t('settings:account.displayName')}</SectionTitle>
                    <SectionCopy>{t('settings:accountEdit.displayNameHelp')}</SectionCopy>
                </SectionHeader>
                <FieldRow>
                    <ProfileInput
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        name="fullName"
                        placeholder={t('settings:accountEdit.displayNamePlaceholder')}
                        disabled={isUpdateFullNameLoading}
                    />
                    <PrimaryButton
                        disabled={isUpdateFullNameLoading}
                        onClick={handleNameSubmit}
                    >
                        {isUpdateFullNameLoading ? <SpinnerSmall /> : t('common:save')}
                    </PrimaryButton>
                </FieldRow>
            </ProfileCard>

            {user.isOrganization && (
                <ProfileCard>
                    <SectionHeader>
                        <SectionTitle>{t('settings:accountEdit.churchUsername')}</SectionTitle>
                        <SectionCopy>{t('settings:accountEdit.churchUsernameHelp')}</SectionCopy>
                    </SectionHeader>
                    <FieldRow>
                        <ProfileInput
                            maxLength={20}
                            value={username}
                            onChange={handleUsernameChange}
                            name="username"
                            placeholder={t('settings:accountEdit.churchUsernamePlaceholder')}
                            disabled={isUpdateUsernameLoading}
                        />
                        <PrimaryButton
                            disabled={isUpdateUsernameLoading}
                            onClick={handleUsernameSubmit}
                        >
                            {isUpdateUsernameLoading ? <SpinnerSmall /> : t('common:save')}
                        </PrimaryButton>
                    </FieldRow>
                    <HintLine>{t('settings:accountEdit.currentUrl', { username: user.name })}</HintLine>
                </ProfileCard>
            )}

            <ProfileCard>
                <SectionHeader>
                    <SectionTitle>{t('settings:account.emailAddress')}</SectionTitle>
                    <SectionCopy>{t('settings:accountEdit.emailHelp')}</SectionCopy>
                </SectionHeader>
                <FieldRow>
                    <ProfileInput
                        onFocus={() => dispatch(setViewResetItemModal(true))}
                        value={isResetEmailSuccess ? newEmail : profile.email}
                        name="email"
                        placeholder={isResetEmailSuccess ? newEmail : profile.email}
                        disabled={viewResetItemModal}
                        readOnly
                    />
                    <PrimaryButton onClick={() => dispatch(setViewResetItemModal(true))}>
                        {t('common:edit')}
                    </PrimaryButton>
                </FieldRow>
            </ProfileCard>

            <ProfileCard>
                <SectionHeader>
                    <SectionTitle>{t('settings:account.password')}</SectionTitle>
                    <SectionCopy>{t('settings:accountEdit.passwordHelp')}</SectionCopy>
                </SectionHeader>
                <PasswordGrid onSubmit={handlePasswordSubmit}>
                    <ProfileInput
                        value={passwords.currentPassword}
                        autoComplete="current-password"
                        type="password"
                        name="currentPassword"
                        placeholder={t('settings:accountEdit.currentPassword')}
                        onChange={handlePasswordChange}
                    />
                    <ProfileInput
                        value={passwords.newPassword}
                        autoComplete="new-password"
                        type="password"
                        name="newPassword"
                        placeholder={t('settings:accountEdit.newPassword')}
                        onChange={handlePasswordChange}
                    />
                    <ProfileInput
                        value={passwords.newPassword2}
                        autoComplete="new-password"
                        type="password"
                        name="newPassword2"
                        placeholder={t('settings:accountEdit.repeatPassword')}
                        onChange={handlePasswordChange}
                    />
                    <PrimaryButton type="submit" disabled={isUpdateLoading}>
                        {isUpdateLoading ? <SpinnerSmall /> : t('settings:account.password')}
                    </PrimaryButton>
                </PasswordGrid>
            </ProfileCard>

            {viewResetItemModal && <ResetSettingsItemModal item="Email" />}
        </ProfileSettingsContainer>
    )
}

const ProfileSettingsContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    color: var(--lightThemeFontColor);
    overflow-y: auto;
    padding: 20px;
    gap: 18px;
`

const ProfileHeader = styled.div`
    width: 100%;
    padding: 0 0 4px 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
`

const ProfileTitle = styled.h1`
    margin: 0;
    font-family: 'Roboto', sans-serif;
    font-size: 24px;
    font-weight: 500;
    color: var(--lightThemeFontColor);
`

const ProfileSubtitle = styled.p`
    margin: 8px 0 0;
    max-width: 520px;
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    line-height: 1.45;
    color: var(--dFontColor);
`

const ProfileCard = styled.section`
    width: 100%;
    background: #fff;
    border: 1px solid var(--borderColor);
    border-radius: 8px;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05);
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`

const SectionTitle = styled.h2`
    margin: 0;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: var(--lightThemeFontColor);
`

const SectionCopy = styled.p`
    margin: 0;
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    line-height: 1.4;
    color: var(--dFontColor);
`

const HintLine = styled.p`
    margin: -4px 0 0;
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    color: var(--dFontColor);
`

const FieldRow = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 110px;
    gap: 10px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`

const PasswordGrid = styled.form`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
`

const ProfileInput = styled.input`
    width: 100%;
    height: 42px;
    border: 1px solid var(--borderColor);
    border-radius: 6px;
    outline: none;
    padding: 0 12px;
    color: var(--lightThemeFontColor);
    background: #fff;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;

    &:focus {
        border-color: var(--themeColor);
        box-shadow: 0 0 0 2px rgba(0, 180, 204, 0.12);
    }

    &:disabled {
        color: var(--dFontColor);
        background: #f5f5f5;
    }
`

const PrimaryButton = styled.button`
    min-width: 110px;
    height: 42px;
    border: 1px solid var(--themeColor);
    border-radius: 6px;
    outline: none;
    background: var(--themeColor);
    color: #fff;
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 14px;

    &:hover {
        background: var(--themeColorDark);
        border-color: var(--themeColorDark);
    }

    &:disabled {
        cursor: default;
        opacity: 0.7;
    }
`
